import * as React from "react";
import { styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
  Fade,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTheme } from "@mui/material/styles";
import CardCategory from "../CardCategory/CardCategory";
import useSectionView from "../hook/useSectionView";

const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))(({ theme }) => ({
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
    minHeight: 2,
  },
  "& .MuiTabs-indicatorSpan": {
    width: "50%",
    backgroundColor: theme.palette.primary.dark,
    borderRadius: 10,
  },
}));

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: "clamp(0.85rem, 1.4vw, 1.1rem)",
    color: "rgba(255,255,255,0.8)",
    minHeight: 44,
    minWidth: 140,
    padding: "6px 12px",
    marginRight: theme.spacing(1),
    // Evitar “salto” de layout al seleccionar
    border: "2px solid transparent",
    borderRadius: 25,
    "&.Mui-selected": {
      color: "#FFFFFF",
      fontWeight: 800,
      borderColor: theme.palette.info.main,
      backgroundColor: "rgba(255,255,255,0.06)",
    },
    "&.Mui-focusVisible": {
      outline: `3px solid ${theme.palette.info.light}`,
      outlineOffset: 2,
    },
    "&:hover": {
      color: theme.palette.primary.contrastText,
      backgroundColor: "rgba(255,255,255,0.08)",
    },
  })
);

const TABS = [
  { label: "Línea hospitalaria", value: "hospitalarios" },
  { label: "Línea farmacéutica", value: "farmaceuticos" },
  { label: "Productos médicos", value: "medicos" },
];

export default function TabsComponent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sectionRef = useSectionView("Productos");

  // Estado con valores semánticos
  const [type, setType] = React.useState("hospitalarios");

  // Lee ?categoria=... si existe y sincroniza URL al cambiar
  React.useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const cat = qs.get("categoria");
    if (cat && TABS.some((t) => t.value === cat)) setType(cat);
  }, []);

  const handleChange = (_e, newValue) => {
    setType(newValue);
    const qs = new URLSearchParams(window.location.search);
    qs.set("categoria", newValue);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${qs.toString()}#Productos`
    );
  };

  return (
    <section id="Productos" ref={sectionRef}>
      <Box
        sx={{
          width: "100%",
          py: 1,
          backgroundImage:
            "linear-gradient(to right top, #2a4e9c, #2365b2, #1d7cc6, #1d93d9, #27aaea)",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            mt: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 900,
              letterSpacing: { xs: "2px", md: "4px" },
              textAlign: "center",
              mb: 2,
            }}
          >
            Nuestros productos
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              px: 2,
              py: 1,
              width: "100%",
            }}
          >
            <StyledTabs
              value={type}
              onChange={handleChange}
              aria-label="Categorías de productos"
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile}
              allowScrollButtonsMobile
              centered={!isMobile}
            >
              {TABS.map((t) => (
                <StyledTab
                  key={t.value}
                  label={t.label}
                  value={t.value}
                  wrapped
                />
              ))}
            </StyledTabs>
          </Box>

          {/* Transición suave del contenido de categoría */}
          <Box sx={{ width: "100%", mt: 2 }}>
            <Fade in key={type} timeout={220}>
              <Box>
                <CardCategory type={type} />
              </Box>
            </Fade>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 4 }}>
            <Button
              component="a"
              href="https://drive.google.com/file/d/1nuAzF3sW7eufSyhFZIUhsltbzC3-8tlZ/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<DownloadIcon />}
              variant="contained"
              sx={{
                bgcolor: theme.palette.info.main,
                px: 3,
                py: 1.2,
                borderRadius: 2,
                color: "#fff",
                "&:hover": { bgcolor: theme.palette.primary.dark },
              }}
              aria-label="Descargar catálogo en PDF"
            >
              Descargar catálogo
            </Button>
          </Box>
        </Container>
      </Box>
    </section>
  );
}
